import { Game } from "@lobby/core/src/entities";
import {
  APIError,
  Button,
  CLIENT_ERRORS,
  ClientError,
  Icon,
  emitter,
  useAuth,
  useMobile,
  useModal,
} from "@lobby/core/src/shared";
import { useTranslate } from "@lobby/ocb-intl";
import { onlineManager } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { PlayerInfo } from "features/player/player-info";
import { TakeBonusButton } from "features/take-bonus-button";
import { useEffect, useRef, useState } from "react";
import { FloatGroup } from "shared/ui";
import { Logo } from "shared/ui/logo";
import { Header } from "widget/header";
import { Jackpots } from "widget/jackpots";
import { MiniGame, MiniGameContextProvider } from "widget/mini-game";
import { isGameExistInHeader as checkGameExistInHeader } from "../lib/helpers";
import { GamePageControlsMobile } from "./game-page-controls.mobile";

import "./styles.css";

export function GamePage() {
  // @ts-ignore
  const { gameId: gameStringId } = useParams({ from: "/_auth/game/$gameId" });
  const navigate = useNavigate();
  const [iframeKey, setIframeKey] = useState<string>(Date.now().toString());

  const openedInNewWindowRef = useRef(false);

  const isMobile = useMobile();

  const { data: categoriesList } = Game.useCategoriesList();
  const { data } = Game.useGameRun({
    gameStringId,
    place: "game_list",
    lang: localStorage.getItem("locale") ?? "en",
    deviceType: isMobile ? "mobile" : "desktop",
    theme: (localStorage.getItem("theme") as "light" | "dark") ?? "dark",
    color: "C69316",
  });

  const gameUrl = data?.result?.url;
  const isNewWindowOpen = data?.result?.isNewWindowOpen === true;

  const isGameExistInHeader =
    categoriesList && checkGameExistInHeader(categoriesList, gameStringId);

  const reloadIframe = () => setIframeKey(Date.now().toString());

  useEffect(() => {
    if (data?.error) {
      const error = data.error;
      navigate({ to: "/casino", replace: true, search: true });

      const apiError = new APIError(error.message, {
        code: error.code,
        game: gameStringId,
      });

      emitter.emit("ERROR", { error: apiError });
    }
  }, [data, navigate, gameStringId]);

  useEffect(() => {
    if (!isNewWindowOpen || !gameUrl || openedInNewWindowRef.current) {
      return;
    }
  
    openedInNewWindowRef.current = true;
  
    const openedWindow = window.open(gameUrl, "_blank");

    if (openedWindow) {
      openedWindow.opener = null;
    }

    void navigate({ to: "/casino", replace: true, search: true });
  }, [gameUrl, isNewWindowOpen, navigate]);

  useEffect(() => {
    const unbinds = [
      emitter.on("THEME_CHANGED", reloadIframe),
      emitter.on("SET_LOCALE", reloadIframe),
    ];

    if (!onlineManager.isOnline()) {
      emitter.emit("ERROR", { error: new ClientError(CLIENT_ERRORS[0]) });
    }

    return () => {
      unbinds.forEach((unbind) => unbind());

      onlineManager.subscribe((isOnline) => {
        if (!isOnline) {
          emitter.emit("ERROR", { error: new ClientError(CLIENT_ERRORS[0]) });
        }
      });
    };
  }, []);

  return (
    <>
      <MiniGameContextProvider>
        <MiniGame />
      </MiniGameContextProvider>

      <div className="game-frame fixed inset-0 z-[100] h-dvh w-screen overflow-y-hidden [&_.xmas-decoration-lights]:hidden">
        <div className="h-full">
          <div className="flex h-full flex-col">
            {isGameExistInHeader ? (
              <Header />
            ) : (
              <GamePageHeader gameStringId={gameStringId} />
            )}
            <div className="grow">
              {gameUrl && !isNewWindowOpen && (
                <IFrame
                  url={gameUrl}
                  meta={iframeKey}
                  gameStringId={gameStringId}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <FloatGroup>
        <TakeBonusButton />
      </FloatGroup>
    </>
  );
}

interface IFrameProps {
  url: string;
  meta: string;
  gameStringId: string;
}

function IFrame({ url, meta, gameStringId }: IFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const navigate = useNavigate();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!iframeRef.current) return;

    const controller = new AbortController();

    /*
      Fortune Panda provider's games redirect to the lobby itself
      on click of the "Back" button it the game. It results having
      casino page opened in the iframe. So it was decided to
      redirect the user to the casino page.
    */
    iframeRef.current.addEventListener(
      "load",
      (e) => {
        const iframe = e.target as HTMLIFrameElement;
        const searchParams = new URLSearchParams(
          iframe?.contentWindow?.location.search
        );

        if (searchParams.get("forceGameExit")) {
          navigate({ to: "/casino", search: true });
        }
      },
      { signal: controller.signal }
    );

    return () => controller.abort();
  }, []);

  return (
    <iframe
      ref={iframeRef}
      key={meta}
      className="size-full"
      id="game-frame"
      title="game-frame"
      src={url}
      // https://git.casinomodule.org/casino23/tickets/-/issues/3382
      allowFullScreen={gameStringId === "btm"}
    />
  );
}

function GamePageHeader({ gameStringId }: { gameStringId: string }) {
  const { $t } = useTranslate();
  const isMobile = useMobile();
  const navigate = useNavigate();
  const { isAuth } = useAuth();
  const { open: openLogInModal } = useModal("log-in");

  const game = Game.getGameByStringId(gameStringId);
  const redeemProviderBalance = Game.useRedeemProviderBalance();

  function onGameClose() {
    if (game?.isWalletIntegration) {
      redeemProviderBalance.mutate({ gameStringId });
    }
  }

  if (isMobile) {
    return (
      <div className="relative mobile-only:p-0.5 p-2.5 [&_.jackpots-ticker\_\_inner]:landscape:py-[0.1875em] [&_.jackpots]:landscape:gap-[0.1875em]">
        <Jackpots />
        <GamePageControlsMobile
          gameStringId={gameStringId}
          onGameClose={onGameClose}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-5 px-5 py-3">
      <Link to="/" className="shrink-0">
        <Logo className="h-11" />
      </Link>
      <div className="grow [&_.jackpots-ticker]:text-[1em]">
        <Jackpots />
      </div>
      <div className="flex h-full items-center gap-5">
        {isAuth ? (
          <PlayerInfo />
        ) : (
          <Button variant="secondary" onClick={openLogInModal}>
            <div className="flex items-center gap-1">
              <span className="mobile-only:hidden">
                {$t({ defaultMessage: "sign in" })}
              </span>
              <Icon name="signIn" className="text-18" />
            </div>
          </Button>
        )}
        <Button
          variant="secondary"
          fit
          onClick={() => {
            onGameClose();
            navigate({ to: "/casino", search: true });
          }}
        >
          <Icon className="text-16" name="close" />
        </Button>
      </div>
    </div>
  );
}
